<script>
import { BBadge, BLink, BModal } from 'bootstrap-vue'
import Button from '@core/components/common/Button.vue'
import HamletTable from '@core/components/common/HamletTable.vue'
import TextEllipsis from '@core/components/common/TextEllipsis.vue'
import { StatisticsApi } from '@core/api'

export default {
    components: {
        Button,
        HamletTable,
        TextEllipsis,
        BBadge,
        BLink,
        BModal,
    },
    props: {
        metric: {
            type: Object,
            default() {
                return { data: [] }
            },
        },
        productId: {
            type: [Number, String],
            required: false,
            default: null,
        },
    },
    data() {
        return {
            columns: [
                {
                    key: 'substance',
                    label: this.$t('Component(Grid):column-name'),
                    tdClass: 'wrap-spaces',
                },
                {
                    key: 'cas',
                    label: this.$t('Component(SubstanceTable):column-cas'),
                },
                {
                    key: 'count',
                    label: this.$t('Component(Grid):column-products-number'),
                },
                {
                    key: 'lists',
                    label: this.$t('Component(Label):substance-list'),
                    active: true,
                },
            ],
            productColumns: [
                {
                    key: 'displayName',
                    label: this.$t('Component(Grid):column-name'),
                    tdClass: 'wrap-spaces',
                },
                {
                    key: 'identifier',
                    label: this.$t('Component(Grid):column-id'),
                    tdClass: 'wrap-spaces',
                },
                {
                    key: 'action',
                    label: '',
                    thClass: 'text-right',
                    tdClass: 'text-right',
                },
            ],
            table: {
                config: null,
                cb: null,
            },
            selectedSubstance: null,
            productsError: false,
        }
    },
    computed: {
        label() {
            if (this.metric.name) {
                const name = `${this.metric.name.type}.${this.metric.name.domain}.${this.metric.name.name}`
                const translation = `Component(Statistics):${name}`
                const t = this.$t(translation)
                if (t !== translation) {
                    return t
                }
            }
            return 'SVHC Overview'
        },
        productsModalTitle() {
            if (!this.selectedSubstance) {
                return ''
            }

            if (this.selectedSubstance.cas) {
                return `${this.selectedSubstance.title} (${this.selectedSubstance.cas})`
            }

            return this.selectedSubstance.title
        },
    },
    watch: {
        metric() {
            if (this.table.config && this.table.cb) {
                this.fetch(this.table.config, this.table.cb)
            }
        },
    },
    methods: {
        onRowClick(item) {
            this.selectedSubstance = item.substance
            this.productsError = false
            this.$refs.productsModal.show()
        },
        onProductsModalHidden() {
            this.selectedSubstance = null
            this.productsError = false
        },
        onTableCreated(config, cb) {
            this.table.config = config
            this.table.cb = cb
        },
        fetch(config, cb) {
            const start = (config.page - 1) * config.perPage
            cb(
                this.metric.data.length,
                this.metric.data.slice(start, start + config.perPage),
            )
        },
        fetchProducts(config, cb) {
            if (!this.selectedSubstance || !this.selectedSubstance.id) {
                cb(0, [])
                return
            }

            this.productsError = false

            this.fetchSubstanceProducts(config)
                .then(response => {
                    cb(response.data.totalItems, response.data.items.map(item => ({
                        ...item,
                        displayName: item.name || item.identifier || '-',
                    })))
                })
                .catch(() => {
                    this.productsError = true
                    cb(0, [])
                })
        },
        fetchSubstanceProducts(config) {
            if (this.productId) {
                return StatisticsApi.getProductSubstanceComponents(
                    this.productId,
                    this.selectedSubstance.id,
                    config.page,
                    config.perPage,
                )
            }

            return StatisticsApi.getSubstanceProducts(this.selectedSubstance.id, config.page, config.perPage)
        },
    },
}
</script>

<template>
    <div>
        <b-modal
            ref="productsModal"
            centered
            size="lg"
            :title="productsModalTitle"
            ok-only
            :ok-title="$t('Component(Button):close')"
            content-class="substance-products-modal"
            hide-header-close
            @hidden="onProductsModalHidden"
        >
            <HamletTable
                v-if="selectedSubstance"
                :key="selectedSubstance.id"
                :columns="productColumns"
                :fetch="fetchProducts"
                border-variant="light"
                card-class="mb-0"
            >
                <template #header-label>
                    {{ $t('Page(ProductOverview):Component(Text):page_title') }}
                </template>

                <template #cell-action="data">
                    <b-link
                        :to="{ name: 'product-card', params: { id: data.item.id } }"
                        target="_blank"
                    >
                        <Button
                            size="sm"
                            variant="outline-primary"
                            class="btn-icon mt-0 mr-0"
                        >
                            <feather-icon icon="ExternalLinkIcon" />
                        </Button>
                    </b-link>
                </template>

                <template #cell-displayName="data">
                    <TextEllipsis
                        :value="data.item.displayName"
                        :max-length="40"
                    />
                </template>

                <template #cell-identifier="data">
                    <TextEllipsis
                        :value="data.item.identifier"
                        :max-length="24"
                    />
                </template>

                <template #no-records>
                    {{
                        productsError
                            ? $t('Component(Text):something-went-wrong')
                            : $t('Component(Text):nothing-found')
                    }}
                </template>
            </HamletTable>
        </b-modal>

        <HamletTable
            :columns="columns"
            :fetch="fetch"
            border-variant="light"
            @created="onTableCreated"
            @onRowClick="onRowClick"
        >
            <template #header-label>
                {{ label }}
            </template>

            <template #cell-cas="data">
                {{ data.item.substance.cas }}
            </template>

            <template #cell-lists="data">
                <b-badge
                    v-for="list in data.item.lists"
                    :key="list.title"
                    class="mr-50"
                >
                    {{ list.title }}
                </b-badge>
            </template>
        </HamletTable>
    </div>
</template>

<style lang="scss">
.substance-products-modal {
    .modal-body {
        padding: 0;
    }
}
</style>
